import { useCallback, useEffect } from 'react';
import { analytics, ANALYTICS_EVENTS, AnalyticsEventName } from '../services/analytics';

/**
 * useAnalytics Hook
 *
 * Provides convenient access to analytics tracking with React hooks.
 *
 * @example
 * ```typescript
 * const { trackEvent, trackPageView } = useAnalytics();
 *
 * // Track an event
 * trackEvent('waitlist_join_clicked', { fair_id: 123 });
 *
 * // Track page view on mount
 * useAnalytics({ autoTrackPageView: true, pageName: '/waitlist' });
 * ```
 */

interface UseAnalyticsOptions {
  /**
   * Automatically track page view on component mount
   */
  autoTrackPageView?: boolean;

  /**
   * Page name for automatic page tracking
   */
  pageName?: string;

  /**
   * Additional properties for automatic page tracking
   */
  pageProperties?: Record<string, unknown>;
}

interface UseAnalyticsReturn {
  /**
   * Track a custom event
   * @param eventName - Name of the event
   * @param properties - Event properties
   */
  trackEvent: (eventName: AnalyticsEventName | string, properties?: Record<string, unknown>) => void;

  /**
   * Track a page view
   * @param path - Page path
   * @param properties - Additional properties
   */
  trackPageView: (path: string, properties?: Record<string, unknown>) => void;

  /**
   * Identify current user (anonymized)
   * @param userId - User ID
   * @param traits - User traits (non-PII)
   */
  identifyUser: (userId: string, traits?: Record<string, unknown>) => void;

  /**
   * Check if analytics is enabled
   */
  isEnabled: boolean;

  /**
   * Event name constants for type safety
   */
  events: typeof ANALYTICS_EVENTS;
}

/**
 * Custom hook for analytics tracking
 *
 * @param options - Configuration options
 * @returns Analytics tracking functions and state
 */
export const useAnalytics = (options: UseAnalyticsOptions = {}): UseAnalyticsReturn => {
  const { autoTrackPageView = false, pageName, pageProperties } = options;

  // Track page view on mount if enabled
  useEffect(() => {
    if (autoTrackPageView && pageName) {
      analytics.page(pageName, pageProperties);
    }
  }, [autoTrackPageView, pageName, pageProperties]);

  // Memoized track event function
  const trackEvent = useCallback((eventName: AnalyticsEventName | string, properties?: Record<string, unknown>) => {
    analytics.track(eventName, properties);
  }, []);

  // Memoized track page view function
  const trackPageView = useCallback((path: string, properties?: Record<string, unknown>) => {
    analytics.page(path, properties);
  }, []);

  // Memoized identify user function
  const identifyUser = useCallback((userId: string, traits?: Record<string, unknown>) => {
    analytics.identify(userId, traits);
  }, []);

  return {
    trackEvent,
    trackPageView,
    identifyUser,
    isEnabled: analytics.isEnabled(),
    events: ANALYTICS_EVENTS,
  };
};

/**
 * useWaitlistAnalytics Hook
 *
 * Specialized hook for waitlist-specific analytics tracking.
 * Provides type-safe event tracking methods for common waitlist interactions.
 *
 * @example
 * ```typescript
 * const { trackJoinClicked, trackSlotOfferAccepted } = useWaitlistAnalytics();
 *
 * trackJoinClicked({ fair_id: 123, fair_name: 'Summer Art Fair' });
 * trackSlotOfferAccepted({ fair_id: 123, position: 5, time_to_decision_seconds: 30 });
 * ```
 */

interface WaitlistAnalyticsProps {
  fair_id?: number;
  fair_name?: string;
  position?: number;
}

interface UseWaitlistAnalyticsReturn {
  // Join events
  trackJoinClicked: (props: { fair_id: number; fair_name: string; current_vendor_count?: number; capacity?: number }) => void;
  trackJoinSuccess: (props: { fair_id: number; position: number; waitlist_length: number }) => void;
  trackJoinFailed: (props: { fair_id: number; error_message: string }) => void;

  // Slot offer events
  trackSlotOfferViewed: (props: { fair_id: number; position: number; time_remaining_hours: number; urgency_level: string }) => void;
  trackSlotOfferAccepted: (props: { fair_id: number; position: number; time_remaining_hours: number; time_to_decision_seconds: number }) => void;
  trackSlotOfferDeclined: (props: { fair_id: number; position: number; time_remaining_hours: number; decline_reason?: string }) => void;
  trackSlotOfferExpired: (props: { fair_id: number; position: number; never_viewed: boolean }) => void;

  // Position change events
  trackPositionImproved: (props: { fair_id: number; old_position: number; new_position: number; improvement_amount: number }) => void;
  trackPositionWorsened: (props: { fair_id: number; old_position: number; new_position: number; decline_amount: number }) => void;

  // Leave events
  trackLeaveClicked: (props: { fair_id: number; position: number; time_on_waitlist_hours: number }) => void;
  trackLeaveSuccess: (props: { fair_id: number; leave_reason?: string }) => void;

  // Countdown events
  trackCountdownUrgencyReached: (props: { fair_id: number; urgency_level: string }) => void;
  trackCountdownViewed: (props: { fair_id: number; time_remaining_hours: number }) => void;

  // Page view events
  trackWaitlistPageViewed: (props: { active_waitlists_count: number; pending_offers_count: number }) => void;
  trackWaitlistModalOpened: (props: { fair_id: number; source: string }) => void;
  trackSlotOfferModalOpened: (props: { fair_id: number; time_remaining_hours: number }) => void;

  // Notification events
  trackNotificationReceived: (props: { notification_type: string; fair_id: number }) => void;
  trackNotificationClicked: (props: { notification_type: string; fair_id: number; time_since_received_seconds: number }) => void;

  // Error events
  trackError: (props: { error_type: string; error_message: string; fair_id?: number; action: string }) => void;
}

/**
 * Specialized hook for waitlist analytics
 *
 * @param defaultProps - Default properties to include with all events
 * @returns Type-safe waitlist event tracking functions
 */
export const useWaitlistAnalytics = (defaultProps: WaitlistAnalyticsProps = {}): UseWaitlistAnalyticsReturn => {
  const { trackEvent } = useAnalytics();

  // Helper to merge default props
  const track = useCallback((eventName: AnalyticsEventName, properties: Record<string, unknown>) => {
    trackEvent(eventName, { ...defaultProps, ...properties });
  }, [trackEvent, defaultProps]);

  return {
    // Join events
    trackJoinClicked: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_JOIN_CLICKED, props), [track]),
    trackJoinSuccess: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_JOIN_SUCCESS, props), [track]),
    trackJoinFailed: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_JOIN_FAILED, props), [track]),

    // Slot offer events
    trackSlotOfferViewed: useCallback((props) => track(ANALYTICS_EVENTS.SLOT_OFFER_VIEWED, props), [track]),
    trackSlotOfferAccepted: useCallback((props) => track(ANALYTICS_EVENTS.SLOT_OFFER_ACCEPTED, props), [track]),
    trackSlotOfferDeclined: useCallback((props) => track(ANALYTICS_EVENTS.SLOT_OFFER_DECLINED, props), [track]),
    trackSlotOfferExpired: useCallback((props) => track(ANALYTICS_EVENTS.SLOT_OFFER_EXPIRED, props), [track]),

    // Position change events
    trackPositionImproved: useCallback((props) => track(ANALYTICS_EVENTS.POSITION_IMPROVED, props), [track]),
    trackPositionWorsened: useCallback((props) => track(ANALYTICS_EVENTS.POSITION_WORSENED, props), [track]),

    // Leave events
    trackLeaveClicked: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_LEAVE_CLICKED, props), [track]),
    trackLeaveSuccess: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_LEAVE_SUCCESS, props), [track]),

    // Countdown events
    trackCountdownUrgencyReached: useCallback((props) => track(ANALYTICS_EVENTS.COUNTDOWN_URGENCY_REACHED, props), [track]),
    trackCountdownViewed: useCallback((props) => track(ANALYTICS_EVENTS.COUNTDOWN_VIEWED, props), [track]),

    // Page view events
    trackWaitlistPageViewed: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_PAGE_VIEWED, props), [track]),
    trackWaitlistModalOpened: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_MODAL_OPENED, props), [track]),
    trackSlotOfferModalOpened: useCallback((props) => track(ANALYTICS_EVENTS.SLOT_OFFER_MODAL_OPENED, props), [track]),

    // Notification events
    trackNotificationReceived: useCallback((props) => track(ANALYTICS_EVENTS.NOTIFICATION_RECEIVED, props), [track]),
    trackNotificationClicked: useCallback((props) => track(ANALYTICS_EVENTS.NOTIFICATION_CLICKED, props), [track]),

    // Error events
    trackError: useCallback((props) => track(ANALYTICS_EVENTS.WAITLIST_ERROR, props), [track]),
  };
};
